import Sortable from "sortablejs";
import { pushNotification } from "../../pushNotification";
import { addCurrentPageEventListener } from "../../../shared/utils";

export function studiesSurveySetup(args) {
  const maxQuestions = 30;

  return {
    study: JSON.parse(JSON.stringify(args.study)), // Deep clone object so we can check if form is dirty later
    studyBatch: args.studyBatch,
    sender: args.sender,
    testSubmitting: false,
    questionModalOpen: false,
    dynamicVisibilityModalOpen: false,
    copyExistingSurveyOpen: false,
    editingQuestionIdx: null,
    editingChildQuestionIdx: null,
    questionFormData: {},
    init() {
      this.initSortable("question-sortable-list");

      addCurrentPageEventListener("dx:copy-existing-survey-questions", (e) => {
        this.study.questions = e.detail.questions;
        this.copyExistingSurveyOpen = false;
      });
    },
    isDirty() {
      return JSON.stringify(args.study) !== JSON.stringify(this.study);
    },
    addQuestion(questionType) {
      this.editingQuestionIdx = null;
      this.editingChildQuestionIdx = null;

      this.questionFormData = {
        type: questionType,
        required: true,
        options: [],
      };

      if (questionType === "multiple_choice") {
        this.questionFormData.options = [
          { name: "Option A" },
          { name: "Option B" },
          { name: "None of the above" },
        ];
      } else if (questionType === "rating_scale") {
        this.questionFormData.options = [
          { name: "Bad" },
          { name: "So-so" },
          { name: "Good" },
        ];
      } else if (
        questionType === "multi_select" ||
        questionType === "multi_select_checkboxes"
      ) {
        this.questionFormData.options = [
          { name: "Option A" },
          { name: "Option B" },
          { name: "None of the above" },
        ];
      }

      this.questionModalOpen = true;

      this.$nextTick(() => {
        this.initSortable("modal-sortable-list", this.questionFormData.options);
      });
    },
    get isValid() {
      return (
        !!this.studyBatch.message_text.trim() &&
        !this.studyBatch.message_text.includes("[TOPIC]") &&
        this.study.questions.length <= maxQuestions &&
        this.study.questions.length > 0 &&
        this.study.questions.every((q) => !this.questionHasInvalidOptions(q))
      );
    },
    get hasTopicPlaceholder() {
      return this.studyBatch.message_text.includes("[TOPIC]");
    },
    removeQuestion(parentIdx, childIdx = null) {
      if (childIdx !== null) {
        this.study.questions[parentIdx].conditionalQuestions.splice(
          childIdx,
          1,
        );
      } else {
        this.study.questions.splice(parentIdx, 1);
      }
    },
    editQuestion(question, parentIdx, childIdx = null) {
      this.questionFormData = JSON.parse(JSON.stringify(question)); // Deep clone question so we don't mutate object
      this.editingQuestionIdx = parentIdx;
      this.editingChildQuestionIdx = childIdx;
      this.questionModalOpen = true;

      this.$nextTick(() => {
        this.initSortable("modal-sortable-list", this.questionFormData.options);
      });
    },
    duplicateQuestion(questionToDuplicate, parentIdx, childIdx = null) {
      const questionCopy = JSON.parse(JSON.stringify(questionToDuplicate)); // Deep clone question so we don't mutate object

      if (childIdx !== null) {
        // Duplicating a child question - add it to parent's conditionalQuestions array
        this.study.questions[parentIdx].conditionalQuestions.splice(
          childIdx,
          0,
          questionCopy,
        );
      } else {
        // Duplicating a parent question - remove any conditionalQuestions from copy
        delete questionCopy.conditionalQuestions;
        this.study.questions.splice(parentIdx, 0, questionCopy);
      }
    },
    openDynamicVisibilityModal(idx) {
      this.editingQuestionIdx = idx;
      this.dynamicVisibilityModalOpen = true;
    },
    handleDynamicVisibilitySubmit(e) {
      e.preventDefault();

      (this.study.questions[this.selectedQuestionIdx].conditionalQuestions ||=
        []).push({
        ...this.study.questions[this.editingQuestionIdx],
        condition: { operator: this.operator, values: this.values },
      });
      this.study.questions.splice(this.editingQuestionIdx, 1);

      this.dynamicVisibilityModalOpen = false;
    },
    questionHasInvalidOptions(question) {
      if (question.type == "short_text" || question.type == "long_text")
        return false;

      return question.options.length < 2;
    },
    changeSender(currentUser) {
      this.sender = currentUser;
    },
    sendTest() {
      this.testSubmitting = true;

      fetch(args.testEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          questions: this.study.questions,
          message_text: this.studyBatch.message_text,
        }),
      })
        .then((response) => {
          this.testSubmitting = false;
          return response.json();
        })
        .then((resp) => {
          if (resp.success) {
            pushNotification({
              title: "Success!",
              desc: `Check ${args.integrationName == "email" ? "your email inbox" : args.integrationName} for a test message.`,
            });
          } else {
            pushNotification({
              title: "Live test failed",
              desc: resp.error_message,
              success: false,
            });
          }
        })
        .catch((error) => {
          console.error(error);
          pushNotification({
            title: "Error",
            desc: "Something went wrong. Please reload the page and try again.",
            success: false,
          });
        });
    },
    get atMaxQuestions() {
      return this.study.questions.length >= maxQuestions;
    },
    getQuestionTypeLabel(type) {
      const typeLabels = {
        short_text: "Short text",
        long_text: "Long text",
        multiple_choice: "Single-select",
        multi_select: "Multi-select dropdown",
        multi_select_checkboxes: "Multi-select checkboxes",
        rating_scale: "Rating scale",
      };
      return typeLabels[type] || type;
    },

    // Stuff for question modal
    get editing() {
      return this.editingQuestionIdx !== null;
    },
    handleSubmit(e) {
      e.preventDefault();

      if (this.editing) {
        if (this.editingChildQuestionIdx !== null) {
          // Editing a child question - update it in parent's conditionalQuestions array
          this.study.questions[this.editingQuestionIdx].conditionalQuestions[
            this.editingChildQuestionIdx
          ] = this.questionFormData;
        } else {
          // Editing a parent question
          this.study.questions[this.editingQuestionIdx] = this.questionFormData;
        }
      } else {
        this.study.questions.push(this.questionFormData);
      }

      this.questionModalOpen = false;
    },
    isDuplicate(name) {
      const { options } = this.questionFormData;
      return options.filter((o) => o.name === name).length > 1;
    },
    addOption() {
      const { options } = this.questionFormData;
      options.push({ name: "" });

      this.$nextTick(() => {
        this.$refs.newOption.focus();
      });
    },
    removeOption(idx) {
      const { options } = this.questionFormData;
      options.splice(idx, 1);
    },
    get questionModalIsValid() {
      const { options, prompt, type } = this.questionFormData;

      const validPrompt = !!prompt?.trim()?.length;

      let validOptions = true;
      if (["multiple_choice", "rating_scale"].includes(type)) {
        validOptions =
          options.length >= 2 &&
          options.every((i) => {
            return (
              i.name !== undefined &&
              i.name !== null &&
              !!i.name?.trim()?.length
            );
          }) &&
          !options.some((item) => this.isDuplicate(item.name));
      }

      return validPrompt && validOptions;
    },

    // This handles sorting for the list of questions AND list of options when editing a question
    reorderArray(array, order) {
      const newArray = new Array(array.length);
      order.forEach((newIndex, originalIndex) => {
        newArray[newIndex] = array[originalIndex];
      });

      return newArray;
    },
    initSortable(listId) {
      const self = this;
      const sortableList = document.getElementById(listId);

      if (!sortableList) return;

      const editableContents =
        sortableList.querySelectorAll(".editable-content");

      new Sortable(sortableList, {
        animation: 150,
        handle: ".handle",
        onStart() {
          editableContents.forEach((content) =>
            content.setAttribute("contenteditable", "false"),
          );
        },
        onEnd() {
          editableContents.forEach((content) =>
            content.setAttribute("contenteditable", "true"),
          );

          const validWrappers = Array.from(sortableList.children).filter(
            (wrapper) => wrapper.querySelector(".sortable-item") !== null,
          );

          const order = validWrappers
            .map(
              (wrapper) => wrapper.querySelector(".sortable-item").dataset.idx,
            )
            .map(Number);

          const toRemove = validWrappers;

          // Remove dom elements (wrapper divs)
          toRemove.forEach((item) => sortableList.removeChild(item));

          // Add dom elements
          const toAdd = self.reorderArray(toRemove, order);
          toAdd.forEach((item) => sortableList.appendChild(item));

          // Update component data
          if (listId == "modal-sortable-list") {
            self.questionFormData.options = order.map(
              (idx) => self.questionFormData.options[idx],
            );
          } else {
            self.study.questions = order.map(
              (idx) => self.study.questions[idx],
            );
          }
        },
      });
    },
  };
}
